import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { TocItem } from "@ebscois/react-component-library";
import { useHistory, useLocation } from "react-router-dom";

// recursive function returns true if this tocItem is active or contains an active child, otherwise returns false.
const checkIsChildActive = (activeItem, subTree = []) => {
  return subTree.some((item) => {
    const isChildActive = checkIsChildActive(activeItem, item.subSection || []);
    return item.reference === activeItem || isChildActive;
  });
};

const ContentTocItem = ({
  content,
  handleTocItemClick,
  breadcrumb,
  isDisabled,
  closeSections,
  loadedSection,
  activeSection,
  activeReference,
  nodeActive,
  depth = 0,
  maxTocDepth,
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const { pathname } = useLocation();
  const history = useHistory();

  const hasSubsections = content.subSection;
  const isActive = activeReference === content.reference;

  // toggle the section open or closed
  const toggleItem = useCallback(() => {
    setIsOpened(!isOpened);
  }, [isOpened]);

  const handleTitleClick = useCallback(() => {
    const reference = content.reference || content.subSection[0].reference;
    handleTocItemClick(reference, content.title, breadcrumb, pathname, history);
    if (hasSubsections && !isOpened) {
      setIsOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    content.reference,
    content.subSection,
    content.title,
    handleTocItemClick,
    breadcrumb,
    pathname,
    history,
    hasSubsections,
    toggleItem,
  ]);

  const handleIconClick = useCallback(() => {
    toggleItem(activeReference);
  }, [toggleItem, activeReference]);

  const isChildActive = checkIsChildActive(activeReference, [content]);

  useEffect(() => {
    if (loadedSection !== content.reference) {
      setIsOpened(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedSection]);

  useEffect(() => {
    if (
      activeSection &&
      activeSection !== content.reference &&
      activeSection !== content.title
    ) {
      setIsOpened(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Detect change in activeTocItem,
  // If the section is active or has an active child open it
  useEffect(() => {
    if (!activeReference) {
      return;
    }
    if (isChildActive) {
      setIsOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeReference,
    content.title,
    content.reference,
    content.subSection,
    checkIsChildActive,
  ]);

  const hasTocDepthLimit = maxTocDepth === 1;
  const isNodeActive =
    depth === 0 && checkIsChildActive(activeReference, [content]);

  return (
    <TocItem
      data-testid={`toc-item-${content.title}`}
      title={content.title}
      isExpanded={isOpened}
      onIconClick={handleIconClick}
      onClick={handleTitleClick}
      active={isNodeActive}
      highlight={isActive}
      isDisabled={isDisabled}
    >
      {hasSubsections &&
        !hasTocDepthLimit &&
        content.subSection.map((subItem) => (
          <ContentTocItem
            key={subItem.reference}
            content={subItem}
            handleTocItemClick={handleTocItemClick}
            breadcrumb={[...(breadcrumb || []), subItem.title]}
            isDisabled={isDisabled}
            activeReference={activeReference}
            nodeActive={nodeActive || isNodeActive}
            depth={depth + 1}
            closeSections={closeSections}
          />
        ))}
    </TocItem>
  );
};

ContentTocItem.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    reference: PropTypes.string.isRequired,
    subSection: PropTypes.array,
  }),
  handleTocItemClick: PropTypes.func.isRequired,
  breadcrumb: PropTypes.array,
  maxTocDepth: PropTypes.number,
};

ContentTocItem.displayName = "ContentTocItem";

export default ContentTocItem;
